// Centralized content config for the BuildRaq single-page site.
import {
  Bot, Workflow, Server, LayoutGrid, Cable, Wrench, Gauge,
  ShieldCheck, Zap, Boxes, LineChart, Receipt, Users,
} from "lucide-react";

export const NAV_LINKS = [
  { label: "Philosophy", href: "#philosophy" },
  { label: "Services", href: "#services" },
  { label: "Products", href: "#products" },
  { label: "Process", href: "#process" },
  { label: "Founder", href: "#founder" },
  { label: "FAQ", href: "#faq" },
];

// Empty until the user adds a scheduler link. When set, "Book a Discovery Call"
// opens it in a new tab; otherwise it smooth-scrolls to the contact form.
export const SCHEDULER_URL = "";

export const HERO = {
  overline: "AI-native engineering studio",
  headlineLines: ["Building Intelligent", "Software for", "Modern Businesses."],
  sub: "BuildRaq designs, engineers, and scales AI systems, enterprise platforms, and automation that companies can actually grow on — production-grade from day one.",
  primaryCta: "Book a Discovery Call",
  secondaryCta: "Explore Our Work",
  stats: [
    { value: "AI-native", label: "engineering DNA" },
    { value: "Prod-grade", label: "from the first commit" },
    { value: "Scalable", label: "systems by design" },
  ],
};

export const MARQUEE_ITEMS = [
  "INTELLIGENT SYSTEMS", "ENTERPRISE SCALE", "AI NATIVE",
  "AUTOMATION", "PRODUCT THINKING", "RELIABLE INFRASTRUCTURE",
];

export const PHILOSOPHY = {
  overline: "Trusted engineering philosophy",
  title: "The name is the mandate.",
  intro: "BuildRaq is two disciplines fused into one operating principle — the craft of building, and the structure that lets it scale.",
  chapters: [
    {
      no: "01",
      key: "Build",
      title: "We build the intelligence.",
      body: "AI systems, autonomous agents, automation, and custom software — engineered to solve real operational problems, not to demo well. Every product ships to production.",
    },
    {
      no: "02",
      key: "Raq",
      title: "We rack the structure.",
      body: "Scalability, reliability, and continuous improvement built into the foundation. The rack is the architecture your business compounds on — resilient under load, ready for tomorrow.",
    },
  ],
  closing: "We build systems companies can grow on.",
};

export const SERVICES = {
  overline: "What we engineer",
  title: "Services measured by outcomes.",
  items: [
    { icon: Bot, title: "AI Agents", outcome: "Automate repetitive operations and augment decision-making with agents that act, not just answer." },
    { icon: Workflow, title: "Workflow Automation", outcome: "Remove manual bottlenecks and compress cycle times across your core business processes." },
    { icon: Server, title: "Backend Engineering", outcome: "Secure, scalable infrastructure engineered to carry your business for years, not sprints." },
    { icon: LayoutGrid, title: "SaaS Development", outcome: "Launch internal or customer-facing platforms with product thinking baked in." },
    { icon: Cable, title: "Enterprise Integrations", outcome: "Connect fragmented tools and data systems into one coherent operating layer." },
    { icon: Gauge, title: "AI-Powered Internal Tools", outcome: "Give teams instant access to insight and action — productivity that compounds." },
    { icon: Wrench, title: "Custom Software", outcome: "Solve unique operational problems with durable software built to your reality." },
  ],
};

export const PRODUCTS = {
  overline: "Featured AI products",
  title: "Agents already earning their keep.",
  intro: "Production-ready AI products, purpose-built for measurable business impact. The lineup is modular — new agents plug in without a redesign.",
  items: [
    {
      tag: "Finance Operations",
      name: "AI Accounts Receivable Agent",
      icon: Receipt,
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxkYXJrJTIwZGFzaGJvYXJkJTIwaW50ZXJmYWNlJTIwZGF0YSUyMGFuYWx5dGljc3xlbnwwfHx8fDE3ODU4MTM0MjN8MA&ixlib=rb-4.1.0&q=85",
      desc: "Turns collections from a manual chase into an autonomous workflow.",
      features: ["Intelligent invoice follow-ups", "Automated payment reminders", "Conversation automation", "Collections workflow assistance"],
    },
    {
      tag: "Growth & Retention",
      name: "AI Customer Retention Agent",
      icon: Users,
      image: "https://images.unsplash.com/photo-1666875753105-c63a6f3bdc86?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxkYXJrJTIwZGFzaGJvYXJkJTIwaW50ZXJmYWNlJTIwZGF0YSUyMGFuYWx5dGljc3xlbnwwfHx8fDE3ODU4MTM0MjN8MA&ixlib=rb-4.1.0&q=85",
      desc: "Predicts churn and orchestrates the response before customers leave.",
      features: ["Churn analysis & scoring", "Proactive customer engagement", "Personalized retention journeys", "Actionable business insights"],
    },
  ],
};

export const WHY = {
  overline: "Why BuildRaq",
  title: "Engineering quality you can build a company on.",
  items: [
    { icon: ShieldCheck, kpi: "Reliability", title: "Production-grade by default", body: "Observability, tests, and resilience are not add-ons. They ship with the first release." },
    { icon: Zap, kpi: "Speed", title: "Momentum without shortcuts", body: "Fast delivery cycles that never trade away architecture or long-term maintainability." },
    { icon: Boxes, kpi: "Architecture", title: "Systems, not scripts", body: "We design for scale and change so today's build doesn't become tomorrow's rewrite." },
    { icon: LineChart, kpi: "Product", title: "Outcome-first thinking", body: "We optimize for business impact and adoption, not feature count or novelty." },
  ],
};

export const PROCESS = {
  overline: "How we deliver",
  title: "From discovery to durable delivery.",
  steps: [
    { no: "01", title: "Discovery", body: "We map the problem, constraints, and the outcome that actually matters to the business." },
    { no: "02", title: "Architecture", body: "We design the system and data model, choosing technology for reliability and scale." },
    { no: "03", title: "Build", body: "Tight iteration loops with working software, transparent progress, and continuous review." },
    { no: "04", title: "Harden", body: "Testing, observability, and performance tuning until it's genuinely production-ready." },
    { no: "05", title: "Scale & Improve", body: "We rack the foundation for growth and iterate on the metrics that move the business." },
  ],
};

export const TECH_STACK = {
  overline: "The stack behind the systems",
  title: "Technology as credibility.",
  groups: [
    { label: "Languages", items: ["Python", "TypeScript", "Go", "Rust"] },
    { label: "AI & ML", items: ["LLM Orchestration", "RAG", "Agent Frameworks", "Vector DBs"] },
    { label: "Backend", items: ["FastAPI", "Node", "PostgreSQL", "MongoDB"] },
    { label: "Cloud & Infra", items: ["AWS", "Docker", "Kubernetes", "Terraform"] },
  ],
};

export const CASE_STUDIES = {
  overline: "Selected work",
  title: "Case studies — landing soon.",
  intro: "Detailed teardowns of the systems we've shipped are in production review. Here's a preview of what's coming.",
  items: [
    {
      image: "https://images.unsplash.com/photo-1565043534426-8a67ac8671e2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBza3lzY3JhcGVyJTIwbmlnaHR8ZW58MHx8fHwxNzg1ODEzNDIzfDA&ixlib=rb-4.1.0&q=85",
      sector: "Fintech",
      title: "Autonomous receivables at enterprise scale",
    },
    {
      image: "https://images.unsplash.com/photo-1453230806017-56d81464b6c5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBza3lzY3JhcGVyJTIwbmlnaHR8ZW58MHx8fHwxNzg1ODEzNDIzfDA&ixlib=rb-4.1.0&q=85",
      sector: "SaaS",
      title: "Retention intelligence for a growth-stage platform",
    },
  ],
};

export const TESTIMONIALS = {
  overline: "Social proof",
  title: "Voices — coming soon.",
  intro: "We're collecting the words of the teams we build with. Placeholders for now, real stories shortly.",
  items: [
    {
      quote: "BuildRaq operates like an internal engineering team that already understands our business.",
      name: "Placeholder Client",
      role: "VP Engineering",
      avatar: "https://images.unsplash.com/photo-1765776830139-72b2184dae5a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHBvcnRyYWl0JTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzg1ODEzNDIzfDA&ixlib=rb-4.1.0&q=85",
    },
    {
      quote: "They shipped a production AI agent while everyone else was still writing proposals.",
      name: "Placeholder Client",
      role: "Founder & CEO",
      avatar: "https://images.unsplash.com/photo-1758534063829-a72058381e21?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHBvcnRyYWl0JTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzg1ODEzNDIzfDA&ixlib=rb-4.1.0&q=85",
    },
    {
      quote: "The systems don't just work — they keep improving. That's the difference.",
      name: "Placeholder Client",
      role: "COO",
      avatar: "https://images.pexels.com/photos/38197025/pexels-photo-38197025.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    },
  ],
};

export const FOUNDER = {
  overline: "Founder story",
  image: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBjb25maWRlbnQlMjBjZW8lMjBwb3J0cmFpdCUyMGRhcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc4NTgxMzQyM3ww&ixlib=rb-4.1.0&q=85",
  title: "Built by engineers who ship.",
  paragraphs: [
    "BuildRaq started with a simple frustration: too much software gets demoed and too little gets shipped. AI made the gap worse — impressive prototypes, fragile production.",
    "So we built a studio around the opposite belief. Intelligence is only valuable when it runs reliably, at scale, inside the messy reality of a real business.",
    "Today we partner with founders and enterprise teams as their engineering-first, AI-native team — building the systems, and racking the structure they grow on.",
  ],
  signature: "The BuildRaq Team",
};

export const FAQS = [
  { q: "What kinds of projects do you take on?", a: "AI agents, workflow automation, backend and infrastructure, SaaS platforms, enterprise integrations, and custom software. If it needs to run reliably in production, it's in scope." },
  { q: "How is BuildRaq different from an agency?", a: "We're an engineering-first studio, not a generalist agency. We think in systems and outcomes, ship production-grade software, and stay for the scaling — not just the launch." },
  { q: "Do you work with early-stage startups and enterprises?", a: "Both. We tailor engagement depth to your stage — from a focused first AI product to long-term platform ownership for enterprise teams." },
  { q: "How quickly can we get started?", a: "A discovery call is the fastest path. We'll scope the problem, propose an architecture, and give you a clear plan — usually within days, not weeks." },
  { q: "How do engagements typically work?", a: "Most start with a focused discovery and a defined first deliverable, then evolve into an ongoing build-and-scale partnership as the system proves value." },
  { q: "Which technologies do you use?", a: "We choose technology for reliability and scale — commonly Python/TypeScript, FastAPI/Node, Postgres/MongoDB, modern LLM and agent frameworks, on AWS with Docker and Kubernetes." },
];

export const PROJECT_TYPES = [
  "AI Agent", "Workflow Automation", "Backend Engineering", "SaaS Platform",
  "Enterprise Integration", "Internal Tools", "Custom Software", "Not sure yet",
];

export const BUDGET_RANGES = [
  "< $10k", "$10k – $25k", "$25k – $75k", "$75k – $150k", "$150k+", "Let's discuss",
];

export const CONTACT = {
  overline: "Start the conversation",
  title: "Book a discovery call.",
  sub: "Tell us what you're building. We'll come back with a clear, engineering-first path forward — usually within one business day.",
};
